// Multi-language content for SNS Infotech International Holdings
// Languages: English, Spanish, German, Japanese, Russian, Chinese, Marathi

export const LANGUAGES = [
  { code: "en", label: "English", native: "English" },
  { code: "es", label: "Spanish", native: "Español" },
  { code: "de", label: "German", native: "Deutsch" },
  { code: "ja", label: "Japanese", native: "日本語" },
  { code: "ru", label: "Russian", native: "Русский" },
  { code: "zh", label: "Chinese", native: "中文" },
  { code: "mr", label: "Marathi", native: "मराठी" },
];

const SERVICE_ICONS = ["shield", "trending-up", "landmark", "boxes", "line-chart"];
const SERVICE_KEYS = ["cyber", "equity", "banking", "blockchain", "fintech"];

export const translations = {
  en: {
    nav: { home: "Home", about: "About", services: "Services", global: "Global Presence", contact: "Contact" },
    common: { learnMore: "Learn more", getInTouch: "Get in touch", explore: "Explore services", readStory: "Read the story", viewOffices: "View offices", talkToUs: "Talk to us" },
    hero: {
      badge: "Global Holdings · Est. 2016",
      title: "Securing capital, code & confidence worldwide",
      subtitle: "SNS Infotech International Holdings unites cybersecurity, private equity, core banking, blockchain and financial software under one trusted global institution.",
      ctaPrimary: "Explore our services",
      ctaSecondary: "Our global presence",
    },
    stats: [
      { value: "6000+", label: "Happy clients worldwide" },
      { value: "40+", label: "Government partners" },
      { value: "5", label: "Core domains" },
      { value: "3", label: "Global offices" },
    ],
    ribbon: "Trusted by governments & 6000+ clients worldwide",
    homeIntro: {
      tag: "Who we are",
      title: "A modern holdings company built for a high-trust world",
      text: "From a headquarters in Luxembourg to offices in California and Dubai, we build and back the technology that runs banks, secures nations and powers the next generation of finance.",
    },
    homeServices: { tag: "What we do", title: "Five domains. One standard of excellence.", subtitle: "Deep expertise across the sectors that demand the highest levels of security and trust." },
    homeGlobal: { tag: "Where we are", title: "A truly global footprint", subtitle: "Headquartered in Luxembourg, operating across three continents." },
    homeFounder: { tag: "Our founder", title: "From a village in Maharashtra to the global stage", text: "Founder Shrikrishna Keskar grew up in a small village in Maharashtra and studied in a Marathi-medium government school. In 2016 he founded SNS Infotech as a cybersecurity and investment firm — today a global holdings institution.", cta: "Read the founder's story" },
    homeWhy: {
      tag: "Why SNS",
      title: "Built on security, scaled by trust",
      points: [
        { title: "Institutional-grade security", text: "Defense-grade practices protecting capital, code and data end to end." },
        { title: "Governments trust us", text: "Serving national institutions and regulators across multiple continents." },
        { title: "Long-term partnership", text: "We invest, build and stay — aligned with our clients for the long run." },
      ],
    },
    cta: { title: "Ready to build something secure together?", text: "Partner with a holdings company that treats your trust as its most valuable asset.", button: "Contact our team" },
    services: {
      heroTitle: "Our Services",
      heroSubtitle: "Five interconnected domains delivering security, capital and technology to institutions worldwide.",
      items: {
        cyber: { name: "Cybersecurity", tagline: "Defense-grade protection", description: "End-to-end cybersecurity for financial institutions, governments and enterprises — threat intelligence, secure architecture, audits and 24/7 monitoring.", features: ["Threat detection & response", "Security audits & compliance", "Zero-trust architecture", "24/7 monitoring"] },
        equity: { name: "Private Equity", tagline: "Capital with conviction", description: "We invest in and grow technology-driven companies, providing long-term capital, operational expertise and access to a global network.", features: ["Growth & buyout capital", "Operational value creation", "Global network access", "Long-term partnership"] },
        banking: { name: "Core Banking Software", tagline: "The engine of modern banks", description: "We design and develop robust core banking platforms that power deposits, lending, payments and compliance at national scale.", features: ["Deposits & lending engines", "Real-time payments", "Regulatory compliance", "Cloud-native & scalable"] },
        blockchain: { name: "Blockchain Technology", tagline: "Trustless, transparent, secure", description: "Enterprise blockchain solutions — from tokenization and smart contracts to secure distributed infrastructure for finance.", features: ["Smart contract development", "Tokenization platforms", "Distributed ledger infra", "Digital asset security"] },
        fintech: { name: "Financial Software Solutions", tagline: "Finance, engineered", description: "Bespoke financial software — trading systems, risk analytics, payment platforms and data infrastructure for the modern economy.", features: ["Trading & risk systems", "Payment platforms", "Data & analytics", "Custom integrations"] },
      },
    },
    about: {
      heroTitle: "About SNS Infotech",
      heroSubtitle: "A global holdings institution founded in 2016 on a simple belief: technology and capital, guided by integrity, can change the world.",
      storyTag: "The founder's story",
      storyTitle: "Shrikrishna Keskar",
      storyRole: "Founder & Chairman",
      storyParas: [
        "Shrikrishna Keskar was born and raised in a small village in Maharashtra, India. He studied in a Marathi-medium government school, far from the world of global finance and technology he would one day help shape.",
        "Driven by curiosity and an unshakeable work ethic, he taught himself the languages of code and capital. In 2016 he founded SNS Infotech as a cybersecurity and investment firm — a bet that trust would become the world's most valuable currency.",
        "Today, SNS Infotech International Holdings operates across cybersecurity, private equity, core banking, blockchain and financial software, serving more than 6000 clients worldwide, including governments across multiple nations.",
      ],
      missionTag: "Mission & Vision",
      missionTitle: "Our Mission",
      missionText: "To secure and empower the institutions that move the world's capital — with technology built on uncompromising trust.",
      visionTitle: "Our Vision",
      visionText: "A world where every nation, bank and enterprise can operate with absolute confidence in the security of their capital and code.",
      valuesTag: "What we stand for",
      valuesTitle: "Our Values",
      values: [
        { title: "Integrity first", text: "We do the right thing, especially when no one is watching." },
        { title: "Security by design", text: "Protection is never an afterthought — it is our foundation." },
        { title: "Global mindset", text: "Local roots, worldwide impact and cultural respect." },
        { title: "Relentless excellence", text: "We hold ourselves to institutional-grade standards, always." },
      ],
      timelineTag: "Our journey",
      timelineTitle: "Milestones",
      timeline: [
        { year: "2016", title: "The beginning", text: "Founded in Luxembourg as a cybersecurity & investment firm." },
        { year: "2016", title: "Global expansion", text: "Offices opened in Watsonville, California and Dubai, UAE." },
        { year: "Today", title: "6000+ clients", text: "Trusted by enterprises and governments worldwide." },
      ],
    },
    global: {
      heroTitle: "Global Presence",
      heroSubtitle: "Headquartered in Luxembourg, with strategic offices across North America and the Middle East.",
      offices: [
        { city: "Luxembourg", country: "Luxembourg", type: "Global Headquarters", desc: "The heart of our operations, at the center of European finance." },
        { city: "Watsonville", country: "California, USA", type: "Technology & Innovation", desc: "Our innovation hub, close to the pulse of global technology." },
        { city: "Dubai", country: "United Arab Emirates", type: "Regional Office", desc: "Serving the Middle East, Asia and emerging markets." },
      ],
    },
    contact: {
      heroTitle: "Contact Us",
      heroSubtitle: "Whether you represent a government, a bank or an enterprise — our team is ready to help.",
      formTitle: "Send us a message",
      name: "Full name",
      email: "Email address",
      company: "Company (optional)",
      subject: "Subject",
      message: "Your message",
      submit: "Send message",
      sending: "Sending...",
      success: "Thank you! Your message has been received. Our team will respond shortly.",
      error: "Something went wrong. Please try again.",
      emailLabel: "Email us",
      hqLabel: "Headquarters",
      officesLabel: "Our offices",
    },
    footer: {
      about: "A global holdings company securing capital, code and confidence across cybersecurity, private equity, banking, blockchain and financial software.",
      quickLinks: "Quick links",
      contactTitle: "Contact",
      rights: "All rights reserved.",
      hq: "Luxembourg (HQ) · Watsonville, USA · Dubai, UAE",
    },
  },
};

// ---- Localized overrides for the remaining languages ----
// To keep the codebase maintainable, every language shares the English
// structure and overrides the visible strings below.

translations.es = {
  nav: { home: "Inicio", about: "Nosotros", services: "Servicios", global: "Presencia global", contact: "Contacto" },
  common: { learnMore: "Saber más", getInTouch: "Contáctanos", explore: "Ver servicios", readStory: "Leer la historia", viewOffices: "Ver oficinas", talkToUs: "Habla con nosotros" },
  hero: {
    badge: "Holding Global · Desde 2016",
    title: "Protegiendo capital, código y confianza en todo el mundo",
    subtitle: "SNS Infotech International Holdings une ciberseguridad, capital privado, banca central, blockchain y software financiero en una sola institución global de confianza.",
    ctaPrimary: "Ver nuestros servicios",
    ctaSecondary: "Nuestra presencia global",
  },
  stats: [
    { value: "6000+", label: "Clientes satisfechos" },
    { value: "40+", label: "Socios gubernamentales" },
    { value: "5", label: "Dominios clave" },
    { value: "3", label: "Oficinas globales" },
  ],
  ribbon: "Con la confianza de gobiernos y más de 6000 clientes en todo el mundo",
  homeIntro: { tag: "Quiénes somos", title: "Un holding moderno para un mundo de alta confianza", text: "Desde nuestra sede en Luxemburgo hasta oficinas en California y Dubái, construimos y respaldamos la tecnología que hace funcionar a los bancos y asegura a las naciones." },
  homeServices: { tag: "Qué hacemos", title: "Cinco dominios. Un estándar de excelencia.", subtitle: "Profunda experiencia en los sectores que exigen los más altos niveles de seguridad y confianza." },
  homeGlobal: { tag: "Dónde estamos", title: "Una huella verdaderamente global", subtitle: "Con sede en Luxemburgo, operando en tres continentes." },
  homeFounder: { tag: "Nuestro fundador", title: "De un pueblo de Maharashtra al escenario mundial", text: "El fundador Shrikrishna Keskar creció en un pequeño pueblo de Maharashtra y estudió en una escuela pública en marathi. En 2016 fundó SNS Infotech como firma de ciberseguridad e inversión.", cta: "Leer la historia del fundador" },
  homeWhy: {
    tag: "Por qué SNS",
    title: "Construido sobre la seguridad, escalado por la confianza",
    points: [
      { title: "Seguridad de nivel institucional", text: "Prácticas de grado militar que protegen capital, código y datos de extremo a extremo." },
      { title: "Los gobiernos confían en nosotros", text: "Servimos a instituciones nacionales y reguladores en varios continentes." },
      { title: "Alianza a largo plazo", text: "Invertimos, construimos y permanecemos, alineados con nuestros clientes." },
    ],
  },
  cta: { title: "¿Listo para construir algo seguro juntos?", text: "Asóciate con un holding que trata tu confianza como su activo más valioso.", button: "Contactar al equipo" },
  services: {
    heroTitle: "Nuestros Servicios",
    heroSubtitle: "Cinco dominios interconectados que brindan seguridad, capital y tecnología a instituciones de todo el mundo.",
    items: {
      cyber: { name: "Ciberseguridad", tagline: "Protección de grado defensivo", description: "Ciberseguridad integral para instituciones financieras, gobiernos y empresas: inteligencia de amenazas, arquitectura segura, auditorías y monitoreo 24/7.", features: ["Detección y respuesta ante amenazas", "Auditorías y cumplimiento", "Arquitectura de confianza cero", "Monitoreo 24/7"] },
      equity: { name: "Capital Privado", tagline: "Capital con convicción", description: "Invertimos y hacemos crecer empresas tecnológicas, aportando capital a largo plazo, experiencia operativa y acceso a una red global.", features: ["Capital de crecimiento", "Creación de valor operativo", "Acceso a red global", "Alianza a largo plazo"] },
      banking: { name: "Software de Banca Central", tagline: "El motor de la banca moderna", description: "Diseñamos plataformas de banca central robustas que impulsan depósitos, préstamos, pagos y cumplimiento a escala nacional.", features: ["Motores de depósitos y préstamos", "Pagos en tiempo real", "Cumplimiento regulatorio", "Nativo en la nube y escalable"] },
      blockchain: { name: "Tecnología Blockchain", tagline: "Sin confianza, transparente, seguro", description: "Soluciones blockchain empresariales, desde tokenización y contratos inteligentes hasta infraestructura distribuida segura para finanzas.", features: ["Contratos inteligentes", "Plataformas de tokenización", "Infraestructura de libro distribuido", "Seguridad de activos digitales"] },
      fintech: { name: "Software Financiero", tagline: "Finanzas, diseñadas", description: "Software financiero a medida: sistemas de trading, análisis de riesgo, plataformas de pago e infraestructura de datos.", features: ["Sistemas de trading y riesgo", "Plataformas de pago", "Datos y analítica", "Integraciones personalizadas"] },
    },
  },
  about: {
    heroTitle: "Sobre SNS Infotech",
    heroSubtitle: "Una institución holding global fundada en 2016 sobre una creencia simple: la tecnología y el capital, guiados por la integridad, pueden cambiar el mundo.",
    storyTag: "La historia del fundador",
    storyTitle: "Shrikrishna Keskar",
    storyRole: "Fundador y Presidente",
    storyParas: [
      "Shrikrishna Keskar nació y creció en un pequeño pueblo de Maharashtra, India. Estudió en una escuela pública en marathi, lejos del mundo de las finanzas y la tecnología globales.",
      "Impulsado por la curiosidad y una ética de trabajo inquebrantable, aprendió por sí mismo los lenguajes del código y del capital. En 2016 fundó SNS Infotech como firma de ciberseguridad e inversión.",
      "Hoy, SNS Infotech International Holdings opera en ciberseguridad, capital privado, banca central, blockchain y software financiero, sirviendo a más de 6000 clientes en todo el mundo, incluidos gobiernos.",
    ],
    missionTag: "Misión y Visión",
    missionTitle: "Nuestra Misión",
    missionText: "Proteger y empoderar a las instituciones que mueven el capital del mundo, con tecnología construida sobre una confianza sin concesiones.",
    visionTitle: "Nuestra Visión",
    visionText: "Un mundo donde cada nación, banco y empresa pueda operar con absoluta confianza en la seguridad de su capital y su código.",
    valuesTag: "Lo que defendemos",
    valuesTitle: "Nuestros Valores",
    values: [
      { title: "Integridad ante todo", text: "Hacemos lo correcto, especialmente cuando nadie mira." },
      { title: "Seguridad por diseño", text: "La protección nunca es una ocurrencia tardía: es nuestra base." },
      { title: "Mentalidad global", text: "Raíces locales, impacto mundial y respeto cultural." },
      { title: "Excelencia incansable", text: "Nos exigimos estándares de nivel institucional, siempre." },
    ],
    timelineTag: "Nuestro camino",
    timelineTitle: "Hitos",
    timeline: [
      { year: "2016", title: "El comienzo", text: "Fundada en Luxemburgo como firma de ciberseguridad e inversión." },
      { year: "2016", title: "Expansión global", text: "Oficinas abiertas en Watsonville, California y Dubái, EAU." },
      { year: "Hoy", title: "Más de 6000 clientes", text: "Con la confianza de empresas y gobiernos de todo el mundo." },
    ],
  },
  global: {
    heroTitle: "Presencia Global",
    heroSubtitle: "Con sede en Luxemburgo y oficinas estratégicas en Norteamérica y Oriente Medio.",
    offices: [
      { city: "Luxemburgo", country: "Luxemburgo", type: "Sede Global", desc: "El corazón de nuestras operaciones, en el centro de las finanzas europeas." },
      { city: "Watsonville", country: "California, EE.UU.", type: "Tecnología e Innovación", desc: "Nuestro centro de innovación, cerca del pulso de la tecnología global." },
      { city: "Dubái", country: "Emiratos Árabes Unidos", type: "Oficina Regional", desc: "Al servicio de Oriente Medio, Asia y mercados emergentes." },
    ],
  },
  contact: {
    heroTitle: "Contáctanos",
    heroSubtitle: "Ya sea que representes a un gobierno, un banco o una empresa, nuestro equipo está listo para ayudar.",
    formTitle: "Envíanos un mensaje",
    name: "Nombre completo",
    email: "Correo electrónico",
    company: "Empresa (opcional)",
    subject: "Asunto",
    message: "Tu mensaje",
    submit: "Enviar mensaje",
    sending: "Enviando...",
    success: "¡Gracias! Hemos recibido tu mensaje. Nuestro equipo responderá pronto.",
    error: "Algo salió mal. Inténtalo de nuevo.",
    emailLabel: "Escríbenos",
    hqLabel: "Sede",
    officesLabel: "Nuestras oficinas",
  },
  footer: {
    about: "Un holding global que protege capital, código y confianza en ciberseguridad, capital privado, banca, blockchain y software financiero.",
    quickLinks: "Enlaces rápidos",
    contactTitle: "Contacto",
    rights: "Todos los derechos reservados.",
    hq: "Luxemburgo (Sede) · Watsonville, EE.UU. · Dubái, EAU",
  },
};

translations.de = {
  nav: { home: "Startseite", about: "Über uns", services: "Leistungen", global: "Globale Präsenz", contact: "Kontakt" },
  common: { learnMore: "Mehr erfahren", getInTouch: "Kontakt aufnehmen", explore: "Leistungen ansehen", readStory: "Geschichte lesen", viewOffices: "Standorte ansehen", talkToUs: "Sprechen Sie mit uns" },
  hero: {
    badge: "Globale Holding · Seit 2016",
    title: "Kapital, Code und Vertrauen weltweit sichern",
    subtitle: "SNS Infotech International Holdings vereint Cybersicherheit, Private Equity, Kernbankensoftware, Blockchain und Finanzsoftware in einer vertrauenswürdigen globalen Institution.",
    ctaPrimary: "Unsere Leistungen",
    ctaSecondary: "Unsere globale Präsenz",
  },
  stats: [
    { value: "6000+", label: "Zufriedene Kunden" },
    { value: "40+", label: "Regierungspartner" },
    { value: "5", label: "Kernbereiche" },
    { value: "3", label: "Globale Standorte" },
  ],
  ribbon: "Vertraut von Regierungen und über 6000 Kunden weltweit",
  homeIntro: { tag: "Wer wir sind", title: "Eine moderne Holding für eine Welt des Vertrauens", text: "Vom Hauptsitz in Luxemburg bis zu Büros in Kalifornien und Dubai bauen und unterstützen wir die Technologie, die Banken betreibt und Nationen absichert." },
  homeServices: { tag: "Was wir tun", title: "Fünf Bereiche. Ein Maßstab für Exzellenz.", subtitle: "Tiefe Expertise in Sektoren, die höchste Sicherheit und Vertrauen erfordern." },
  homeGlobal: { tag: "Wo wir sind", title: "Eine wahrhaft globale Präsenz", subtitle: "Hauptsitz in Luxemburg, tätig auf drei Kontinenten." },
  homeFounder: { tag: "Unser Gründer", title: "Von einem Dorf in Maharashtra auf die globale Bühne", text: "Gründer Shrikrishna Keskar wuchs in einem kleinen Dorf in Maharashtra auf und besuchte eine staatliche Schule mit Marathi als Unterrichtssprache. 2016 gründete er SNS Infotech als Cybersicherheits- und Investmentfirma.", cta: "Die Geschichte des Gründers lesen" },
  homeWhy: {
    tag: "Warum SNS",
    title: "Auf Sicherheit gebaut, durch Vertrauen skaliert",
    points: [
      { title: "Sicherheit auf Institutionsniveau", text: "Praktiken auf Verteidigungsniveau, die Kapital, Code und Daten durchgängig schützen." },
      { title: "Regierungen vertrauen uns", text: "Wir betreuen nationale Institutionen und Regulierungsbehörden auf mehreren Kontinenten." },
      { title: "Langfristige Partnerschaft", text: "Wir investieren, bauen und bleiben – im Einklang mit unseren Kunden." },
    ],
  },
  cta: { title: "Bereit, gemeinsam etwas Sicheres zu bauen?", text: "Arbeiten Sie mit einer Holding, die Ihr Vertrauen als wertvollstes Gut behandelt.", button: "Team kontaktieren" },
  services: {
    heroTitle: "Unsere Leistungen",
    heroSubtitle: "Fünf vernetzte Bereiche, die Institutionen weltweit Sicherheit, Kapital und Technologie bieten.",
    items: {
      cyber: { name: "Cybersicherheit", tagline: "Schutz auf Verteidigungsniveau", description: "Umfassende Cybersicherheit für Finanzinstitute, Regierungen und Unternehmen – Bedrohungsanalyse, sichere Architektur, Audits und 24/7-Überwachung.", features: ["Bedrohungserkennung & -abwehr", "Audits & Compliance", "Zero-Trust-Architektur", "24/7-Überwachung"] },
      equity: { name: "Private Equity", tagline: "Kapital mit Überzeugung", description: "Wir investieren in technologiegetriebene Unternehmen und lassen sie wachsen – mit langfristigem Kapital, operativer Expertise und globalem Netzwerk.", features: ["Wachstumskapital", "Operative Wertschöpfung", "Globaler Netzwerkzugang", "Langfristige Partnerschaft"] },
      banking: { name: "Kernbankensoftware", tagline: "Der Motor moderner Banken", description: "Wir entwickeln robuste Kernbankenplattformen für Einlagen, Kredite, Zahlungen und Compliance im nationalen Maßstab.", features: ["Einlagen- & Kreditsysteme", "Echtzeitzahlungen", "Regulatorische Compliance", "Cloud-nativ & skalierbar"] },
      blockchain: { name: "Blockchain-Technologie", tagline: "Vertrauenslos, transparent, sicher", description: "Unternehmens-Blockchain-Lösungen – von Tokenisierung und Smart Contracts bis zu sicherer verteilter Infrastruktur.", features: ["Smart-Contract-Entwicklung", "Tokenisierungsplattformen", "Distributed-Ledger-Infrastruktur", "Sicherheit digitaler Assets"] },
      fintech: { name: "Finanzsoftware-Lösungen", tagline: "Finanzen, konstruiert", description: "Maßgeschneiderte Finanzsoftware – Handelssysteme, Risikoanalytik, Zahlungsplattformen und Dateninfrastruktur.", features: ["Handels- & Risikosysteme", "Zahlungsplattformen", "Daten & Analytik", "Individuelle Integrationen"] },
    },
  },
  about: {
    heroTitle: "Über SNS Infotech",
    heroSubtitle: "Eine 2016 gegründete globale Holding, basierend auf einer einfachen Überzeugung: Technologie und Kapital, geleitet von Integrität, können die Welt verändern.",
    storyTag: "Die Geschichte des Gründers",
    storyTitle: "Shrikrishna Keskar",
    storyRole: "Gründer & Vorsitzender",
    storyParas: [
      "Shrikrishna Keskar wurde in einem kleinen Dorf in Maharashtra, Indien, geboren und wuchs dort auf. Er besuchte eine staatliche Schule mit Marathi als Unterrichtssprache – weit entfernt von der Welt der globalen Finanzen und Technologie.",
      "Angetrieben von Neugier und einer unerschütterlichen Arbeitsmoral brachte er sich die Sprachen des Codes und des Kapitals selbst bei. 2016 gründete er SNS Infotech als Cybersicherheits- und Investmentfirma.",
      "Heute ist SNS Infotech International Holdings in Cybersicherheit, Private Equity, Kernbanken, Blockchain und Finanzsoftware tätig und betreut über 6000 Kunden weltweit, darunter Regierungen.",
    ],
    missionTag: "Mission & Vision",
    missionTitle: "Unsere Mission",
    missionText: "Die Institutionen zu sichern und zu stärken, die das Kapital der Welt bewegen – mit Technologie, die auf kompromisslosem Vertrauen basiert.",
    visionTitle: "Unsere Vision",
    visionText: "Eine Welt, in der jede Nation, Bank und jedes Unternehmen mit absolutem Vertrauen in die Sicherheit von Kapital und Code arbeiten kann.",
    valuesTag: "Wofür wir stehen",
    valuesTitle: "Unsere Werte",
    values: [
      { title: "Integrität zuerst", text: "Wir tun das Richtige – besonders wenn niemand hinsieht." },
      { title: "Sicherheit von Anfang an", text: "Schutz ist nie nachträglich – er ist unser Fundament." },
      { title: "Globale Denkweise", text: "Lokale Wurzeln, weltweite Wirkung und kultureller Respekt." },
      { title: "Unermüdliche Exzellenz", text: "Wir halten uns stets an Standards auf Institutionsniveau." },
    ],
    timelineTag: "Unser Weg",
    timelineTitle: "Meilensteine",
    timeline: [
      { year: "2016", title: "Der Anfang", text: "In Luxemburg als Cybersicherheits- & Investmentfirma gegründet." },
      { year: "2016", title: "Globale Expansion", text: "Büros in Watsonville, Kalifornien und Dubai, VAE eröffnet." },
      { year: "Heute", title: "Über 6000 Kunden", text: "Vertraut von Unternehmen und Regierungen weltweit." },
    ],
  },
  global: {
    heroTitle: "Globale Präsenz",
    heroSubtitle: "Hauptsitz in Luxemburg, mit strategischen Büros in Nordamerika und dem Nahen Osten.",
    offices: [
      { city: "Luxemburg", country: "Luxemburg", type: "Globaler Hauptsitz", desc: "Das Herz unserer Aktivitäten, im Zentrum der europäischen Finanzwelt." },
      { city: "Watsonville", country: "Kalifornien, USA", type: "Technologie & Innovation", desc: "Unser Innovationszentrum, nah am Puls der globalen Technologie." },
      { city: "Dubai", country: "Vereinigte Arabische Emirate", type: "Regionalbüro", desc: "Für den Nahen Osten, Asien und aufstrebende Märkte." },
    ],
  },
  contact: {
    heroTitle: "Kontakt",
    heroSubtitle: "Ob Sie eine Regierung, eine Bank oder ein Unternehmen vertreten – unser Team ist für Sie da.",
    formTitle: "Senden Sie uns eine Nachricht",
    name: "Vollständiger Name",
    email: "E-Mail-Adresse",
    company: "Unternehmen (optional)",
    subject: "Betreff",
    message: "Ihre Nachricht",
    submit: "Nachricht senden",
    sending: "Wird gesendet...",
    success: "Vielen Dank! Ihre Nachricht ist eingegangen. Unser Team meldet sich in Kürze.",
    error: "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
    emailLabel: "E-Mail an uns",
    hqLabel: "Hauptsitz",
    officesLabel: "Unsere Standorte",
  },
  footer: {
    about: "Eine globale Holding, die Kapital, Code und Vertrauen in Cybersicherheit, Private Equity, Banking, Blockchain und Finanzsoftware sichert.",
    quickLinks: "Schnelllinks",
    contactTitle: "Kontakt",
    rights: "Alle Rechte vorbehalten.",
    hq: "Luxemburg (HQ) · Watsonville, USA · Dubai, VAE",
  },
};

translations.ja = {
  nav: { home: "ホーム", about: "会社概要", services: "サービス", global: "グローバル拠点", contact: "お問い合わせ" },
  common: { learnMore: "詳しく見る", getInTouch: "お問い合わせ", explore: "サービスを見る", readStory: "物語を読む", viewOffices: "拠点を見る", talkToUs: "ご相談ください" },
  hero: {
    badge: "グローバル・ホールディングス · 2016年設立",
    title: "資本・コード・信頼を世界中で守る",
    subtitle: "SNS Infotech International Holdingsは、サイバーセキュリティ、プライベートエクイティ、コアバンキング、ブロックチェーン、金融ソフトウェアを一つの信頼される国際機関に統合します。",
    ctaPrimary: "サービスを見る",
    ctaSecondary: "グローバル拠点",
  },
  stats: [
    { value: "6000+", label: "満足したクライアント" },
    { value: "40+", label: "政府パートナー" },
    { value: "5", label: "主要領域" },
    { value: "3", label: "グローバル拠点" },
  ],
  ribbon: "各国政府と世界6000社以上のクライアントから信頼されています",
  homeIntro: { tag: "私たちについて", title: "高い信頼を必要とする世界のためのホールディングス", text: "ルクセンブルクの本社からカリフォルニア、ドバイのオフィスまで、銀行を動かし国家を守るテクノロジーを構築・支援します。" },
  homeServices: { tag: "事業内容", title: "5つの領域、一つの卓越性の基準", subtitle: "最高レベルのセキュリティと信頼を要する分野における深い専門知識。" },
  homeGlobal: { tag: "拠点", title: "真にグローバルな展開", subtitle: "ルクセンブルクに本社を置き、3大陸で事業を展開。" },
  homeFounder: { tag: "創業者", title: "マハラシュトラの村から世界の舞台へ", text: "創業者シュリクリシュナ・ケスカルは、マハラシュトラの小さな村で育ち、マラーティー語の公立学校で学びました。2016年、サイバーセキュリティと投資の会社としてSNS Infotechを設立しました。", cta: "創業者の物語を読む" },
  homeWhy: {
    tag: "なぜSNSか",
    title: "セキュリティを基盤に、信頼で拡大",
    points: [
      { title: "機関レベルのセキュリティ", text: "資本・コード・データを端から端まで守る防衛級の実践。" },
      { title: "政府からの信頼", text: "複数の大陸で国家機関や規制当局にサービスを提供。" },
      { title: "長期的なパートナーシップ", text: "投資し、構築し、留まる。クライアントと長期的に歩みます。" },
    ],
  },
  cta: { title: "安全なものを一緒に築きませんか？", text: "あなたの信頼を最も価値ある資産として扱うホールディングスと提携を。", button: "チームに連絡する" },
  services: {
    heroTitle: "サービス",
    heroSubtitle: "世界中の機関にセキュリティ、資本、テクノロジーを提供する、相互に連携した5つの領域。",
    items: {
      cyber: { name: "サイバーセキュリティ", tagline: "防衛級の保護", description: "金融機関、政府、企業向けの包括的なサイバーセキュリティ。脅威インテリジェンス、安全な設計、監査、24時間監視。", features: ["脅威の検知と対応", "監査とコンプライアンス", "ゼロトラスト設計", "24時間監視"] },
      equity: { name: "プライベートエクイティ", tagline: "信念ある資本", description: "テクノロジー主導の企業に投資し成長させ、長期資本、運営の専門知識、グローバルネットワークを提供します。", features: ["成長資本", "運営価値の創出", "グローバルネットワーク", "長期パートナーシップ"] },
      banking: { name: "コアバンキング・ソフトウェア", tagline: "現代銀行のエンジン", description: "預金、融資、決済、コンプライアンスを国家規模で支える堅牢なコアバンキング基盤を設計・開発します。", features: ["預金・融資エンジン", "リアルタイム決済", "規制コンプライアンス", "クラウドネイティブで拡張可能"] },
      blockchain: { name: "ブロックチェーン技術", tagline: "信頼不要・透明・安全", description: "トークン化やスマートコントラクトから、金融向けの安全な分散型インフラまで、企業向けブロックチェーンソリューション。", features: ["スマートコントラクト開発", "トークン化基盤", "分散台帳インフラ", "デジタル資産のセキュリティ"] },
      fintech: { name: "金融ソフトウェア", tagline: "設計された金融", description: "取引システム、リスク分析、決済基盤、データ基盤など、オーダーメイドの金融ソフトウェア。", features: ["取引・リスクシステム", "決済プラットフォーム", "データと分析", "カスタム統合"] },
    },
  },
  about: {
    heroTitle: "SNS Infotechについて",
    heroSubtitle: "2016年設立のグローバル・ホールディングス。誠実さに導かれたテクノロジーと資本は世界を変えられるという信念に基づいています。",
    storyTag: "創業者の物語",
    storyTitle: "シュリクリシュナ・ケスカル",
    storyRole: "創業者兼会長",
    storyParas: [
      "シュリクリシュナ・ケスカルは、インド・マハラシュトラ州の小さな村で生まれ育ちました。世界の金融やテクノロジーとはかけ離れた、マラーティー語の公立学校で学びました。",
      "好奇心と揺るがぬ勤勉さに突き動かされ、彼はコードと資本の言語を独学で習得しました。2016年、彼はSNS Infotechをサイバーセキュリティと投資の会社として設立しました。",
      "今日、SNS Infotech International Holdingsはサイバーセキュリティ、プライベートエクイティ、コアバンキング、ブロックチェーン、金融ソフトウェアを手がけ、政府を含む世界6000社以上のクライアントにサービスを提供しています。",
    ],
    missionTag: "ミッションとビジョン",
    missionTitle: "私たちのミッション",
    missionText: "世界の資本を動かす機関を、妥協なき信頼の上に築かれたテクノロジーで守り、力づけること。",
    visionTitle: "私たちのビジョン",
    visionText: "すべての国家、銀行、企業が、資本とコードの安全性に絶対的な信頼を持って活動できる世界。",
    valuesTag: "私たちの信条",
    valuesTitle: "価値観",
    values: [
      { title: "誠実さを第一に", text: "誰も見ていない時こそ、正しいことを行います。" },
      { title: "設計段階からの安全性", text: "保護は後付けではなく、私たちの基盤です。" },
      { title: "グローバルな視点", text: "地域に根ざし、世界に影響を与え、文化を尊重します。" },
      { title: "たゆまぬ卓越性", text: "常に機関レベルの基準を自らに課します。" },
    ],
    timelineTag: "私たちの歩み",
    timelineTitle: "マイルストーン",
    timeline: [
      { year: "2016", title: "始まり", text: "ルクセンブルクでサイバーセキュリティと投資の会社として設立。" },
      { year: "2016", title: "グローバル展開", text: "カリフォルニア州ワトソンビルとUAEドバイに拠点開設。" },
      { year: "現在", title: "6000社以上のクライアント", text: "世界中の企業と政府から信頼されています。" },
    ],
  },
  global: {
    heroTitle: "グローバル拠点",
    heroSubtitle: "ルクセンブルクに本社を置き、北米と中東に戦略拠点を構えています。",
    offices: [
      { city: "ルクセンブルク", country: "ルクセンブルク", type: "グローバル本社", desc: "欧州金融の中心にある、私たちの事業の心臓部。" },
      { city: "ワトソンビル", country: "カリフォルニア州, 米国", type: "テクノロジーとイノベーション", desc: "世界のテクノロジーの鼓動に近い、私たちのイノベーション拠点。" },
      { city: "ドバイ", country: "アラブ首長国連邦", type: "地域オフィス", desc: "中東、アジア、新興市場にサービスを提供。" },
    ],
  },
  contact: {
    heroTitle: "お問い合わせ",
    heroSubtitle: "政府、銀行、企業のいずれをご代表でも、私たちのチームがお手伝いします。",
    formTitle: "メッセージを送る",
    name: "お名前",
    email: "メールアドレス",
    company: "会社名（任意）",
    subject: "件名",
    message: "メッセージ",
    submit: "送信する",
    sending: "送信中...",
    success: "ありがとうございます。メッセージを受け取りました。まもなくご返信いたします。",
    error: "問題が発生しました。もう一度お試しください。",
    emailLabel: "メールする",
    hqLabel: "本社",
    officesLabel: "拠点一覧",
  },
  footer: {
    about: "サイバーセキュリティ、プライベートエクイティ、銀行、ブロックチェーン、金融ソフトウェアにわたり、資本・コード・信頼を守るグローバル・ホールディングス。",
    quickLinks: "クイックリンク",
    contactTitle: "連絡先",
    rights: "全著作権所有。",
    hq: "ルクセンブルク（本社） · ワトソンビル, 米国 · ドバイ, UAE",
  },
};

translations.ru = {
  nav: { home: "Главная", about: "О компании", services: "Услуги", global: "Глобальное присутствие", contact: "Контакты" },
  common: { learnMore: "Подробнее", getInTouch: "Связаться", explore: "Наши услуги", readStory: "Читать историю", viewOffices: "Наши офисы", talkToUs: "Свяжитесь с нами" },
  hero: {
    badge: "Глобальный холдинг · с 2016",
    title: "Защищаем капитал, код и доверие по всему миру",
    subtitle: "SNS Infotech International Holdings объединяет кибербезопасность, прямые инвестиции, банковское ПО, блокчейн и финансовое программное обеспечение в одном надёжном глобальном институте.",
    ctaPrimary: "Наши услуги",
    ctaSecondary: "Глобальное присутствие",
  },
  stats: [
    { value: "6000+", label: "Довольных клиентов" },
    { value: "40+", label: "Правительственных партнёров" },
    { value: "5", label: "Ключевых направлений" },
    { value: "3", label: "Глобальных офиса" },
  ],
  ribbon: "Нам доверяют правительства и более 6000 клиентов по всему миру",
  homeIntro: { tag: "Кто мы", title: "Современный холдинг для мира высокого доверия", text: "От штаб-квартиры в Люксембурге до офисов в Калифорнии и Дубае мы создаём и поддерживаем технологии, которые управляют банками и защищают государства." },
  homeServices: { tag: "Чем мы занимаемся", title: "Пять направлений. Один стандарт совершенства.", subtitle: "Глубокая экспертиза в секторах, требующих высочайшего уровня безопасности и доверия." },
  homeGlobal: { tag: "Где мы", title: "По-настоящему глобальное присутствие", subtitle: "Штаб-квартира в Люксембурге, работа на трёх континентах." },
  homeFounder: { tag: "Наш основатель", title: "Из деревни в Махараштре на мировую сцену", text: "Основатель Шрикришна Кескар вырос в маленькой деревне в Махараштре и учился в государственной школе на языке маратхи. В 2016 году он основал SNS Infotech как фирму по кибербезопасности и инвестициям.", cta: "Читать историю основателя" },
  homeWhy: {
    tag: "Почему SNS",
    title: "Построено на безопасности, масштабировано доверием",
    points: [
      { title: "Безопасность институционального уровня", text: "Практики оборонного класса, защищающие капитал, код и данные от начала до конца." },
      { title: "Нам доверяют правительства", text: "Мы обслуживаем национальные институты и регуляторов на нескольких континентах." },
      { title: "Долгосрочное партнёрство", text: "Мы инвестируем, строим и остаёмся вместе с нашими клиентами надолго." },
    ],
  },
  cta: { title: "Готовы построить нечто безопасное вместе?", text: "Станьте партнёром холдинга, для которого ваше доверие — самый ценный актив.", button: "Связаться с командой" },
  services: {
    heroTitle: "Наши услуги",
    heroSubtitle: "Пять взаимосвязанных направлений, обеспечивающих безопасность, капитал и технологии для институтов по всему миру.",
    items: {
      cyber: { name: "Кибербезопасность", tagline: "Защита оборонного класса", description: "Комплексная кибербезопасность для финансовых институтов, правительств и предприятий — аналитика угроз, безопасная архитектура, аудиты и мониторинг 24/7.", features: ["Обнаружение и реагирование на угрозы", "Аудиты и комплаенс", "Архитектура нулевого доверия", "Мониторинг 24/7"] },
      equity: { name: "Прямые инвестиции", tagline: "Капитал с убеждением", description: "Мы инвестируем в технологические компании и развиваем их, предоставляя долгосрочный капитал, операционную экспертизу и доступ к глобальной сети.", features: ["Капитал для роста", "Создание операционной ценности", "Доступ к глобальной сети", "Долгосрочное партнёрство"] },
      banking: { name: "Банковское ПО", tagline: "Двигатель современных банков", description: "Мы проектируем надёжные банковские платформы для депозитов, кредитования, платежей и комплаенса национального масштаба.", features: ["Системы депозитов и кредитов", "Платежи в реальном времени", "Регуляторный комплаенс", "Облачная и масштабируемая"] },
      blockchain: { name: "Блокчейн-технологии", tagline: "Без доверия, прозрачно, безопасно", description: "Корпоративные блокчейн-решения — от токенизации и смарт-контрактов до безопасной распределённой инфраструктуры для финансов.", features: ["Разработка смарт-контрактов", "Платформы токенизации", "Инфраструктура распределённого реестра", "Безопасность цифровых активов"] },
      fintech: { name: "Финансовое ПО", tagline: "Финансы, спроектированные", description: "Индивидуальное финансовое ПО — торговые системы, риск-аналитика, платёжные платформы и инфраструктура данных.", features: ["Торговые и риск-системы", "Платёжные платформы", "Данные и аналитика", "Индивидуальные интеграции"] },
    },
  },
  about: {
    heroTitle: "О SNS Infotech",
    heroSubtitle: "Глобальный холдинг, основанный в 2016 году на простом убеждении: технологии и капитал, ведомые честностью, могут изменить мир.",
    storyTag: "История основателя",
    storyTitle: "Шрикришна Кескар",
    storyRole: "Основатель и председатель",
    storyParas: [
      "Шрикришна Кескар родился и вырос в маленькой деревне в Махараштре, Индия. Он учился в государственной школе на языке маратхи, вдали от мира глобальных финансов и технологий.",
      "Движимый любопытством и непоколебимой трудовой этикой, он самостоятельно освоил языки кода и капитала. В 2016 году он основал SNS Infotech как фирму по кибербезопасности и инвестициям.",
      "Сегодня SNS Infotech International Holdings работает в сферах кибербезопасности, прямых инвестиций, банковского ПО, блокчейна и финансового ПО, обслуживая более 6000 клиентов по всему миру, включая правительства.",
    ],
    missionTag: "Миссия и видение",
    missionTitle: "Наша миссия",
    missionText: "Защищать и укреплять институты, которые движут мировым капиталом, с помощью технологий, построенных на бескомпромиссном доверии.",
    visionTitle: "Наше видение",
    visionText: "Мир, где каждая нация, банк и предприятие могут работать с абсолютной уверенностью в безопасности своего капитала и кода.",
    valuesTag: "Наши принципы",
    valuesTitle: "Наши ценности",
    values: [
      { title: "Честность прежде всего", text: "Мы поступаем правильно, особенно когда никто не видит." },
      { title: "Безопасность по замыслу", text: "Защита — не запоздалая мысль, а наша основа." },
      { title: "Глобальное мышление", text: "Локальные корни, всемирное влияние и уважение к культурам." },
      { title: "Неустанное совершенство", text: "Мы всегда придерживаемся стандартов институционального уровня." },
    ],
    timelineTag: "Наш путь",
    timelineTitle: "Вехи",
    timeline: [
      { year: "2016", title: "Начало", text: "Основана в Люксембурге как фирма по кибербезопасности и инвестициям." },
      { year: "2016", title: "Глобальная экспансия", text: "Открыты офисы в Уотсонвилле, Калифорния и Дубае, ОАЭ." },
      { year: "Сегодня", title: "6000+ клиентов", text: "Нам доверяют предприятия и правительства по всему миру." },
    ],
  },
  global: {
    heroTitle: "Глобальное присутствие",
    heroSubtitle: "Штаб-квартира в Люксембурге, со стратегическими офисами в Северной Америке и на Ближнем Востоке.",
    offices: [
      { city: "Люксембург", country: "Люксембург", type: "Глобальная штаб-квартира", desc: "Сердце наших операций в центре европейских финансов." },
      { city: "Уотсонвилл", country: "Калифорния, США", type: "Технологии и инновации", desc: "Наш центр инноваций, рядом с пульсом мировых технологий." },
      { city: "Дубай", country: "Объединённые Арабские Эмираты", type: "Региональный офис", desc: "Обслуживание Ближнего Востока, Азии и развивающихся рынков." },
    ],
  },
  contact: {
    heroTitle: "Свяжитесь с нами",
    heroSubtitle: "Представляете ли вы правительство, банк или предприятие — наша команда готова помочь.",
    formTitle: "Отправьте нам сообщение",
    name: "Полное имя",
    email: "Адрес эл. почты",
    company: "Компания (необязательно)",
    subject: "Тема",
    message: "Ваше сообщение",
    submit: "Отправить",
    sending: "Отправка...",
    success: "Спасибо! Ваше сообщение получено. Наша команда скоро ответит.",
    error: "Что-то пошло не так. Пожалуйста, попробуйте снова.",
    emailLabel: "Напишите нам",
    hqLabel: "Штаб-квартира",
    officesLabel: "Наши офисы",
  },
  footer: {
    about: "Глобальный холдинг, защищающий капитал, код и доверие в сферах кибербезопасности, прямых инвестиций, банкинга, блокчейна и финансового ПО.",
    quickLinks: "Быстрые ссылки",
    contactTitle: "Контакты",
    rights: "Все права защищены.",
    hq: "Люксембург (штаб-квартира) · Уотсонвилл, США · Дубай, ОАЭ",
  },
};

translations.zh = {
  nav: { home: "首页", about: "关于我们", services: "服务", global: "全球布局", contact: "联系我们" },
  common: { learnMore: "了解更多", getInTouch: "联系我们", explore: "查看服务", readStory: "阅读故事", viewOffices: "查看办公室", talkToUs: "与我们联系" },
  hero: {
    badge: "全球控股 · 成立于2016",
    title: "在全球守护资本、代码与信任",
    subtitle: "SNS Infotech International Holdings 将网络安全、私募股权、核心银行、区块链和金融软件整合于一家值得信赖的全球机构之中。",
    ctaPrimary: "探索我们的服务",
    ctaSecondary: "我们的全球布局",
  },
  stats: [
    { value: "6000+", label: "满意客户" },
    { value: "40+", label: "政府合作伙伴" },
    { value: "5", label: "核心领域" },
    { value: "3", label: "全球办公室" },
  ],
  ribbon: "受到各国政府及全球6000多家客户的信赖",
  homeIntro: { tag: "我们是谁", title: "为高信任世界打造的现代控股公司", text: "从卢森堡总部到加州和迪拜的办公室，我们构建并支持运行银行、守护国家的技术。" },
  homeServices: { tag: "我们的业务", title: "五大领域，同一卓越标准", subtitle: "在要求最高安全与信任的行业拥有深厚专业能力。" },
  homeGlobal: { tag: "我们的位置", title: "真正的全球版图", subtitle: "总部位于卢森堡，业务遍及三大洲。" },
  homeFounder: { tag: "我们的创始人", title: "从马哈拉施特拉的乡村走向世界舞台", text: "创始人 Shrikrishna Keskar 在马哈拉施特拉一个小村庄长大，就读于马拉地语公立学校。2016年，他创立了 SNS Infotech，最初是一家网络安全与投资公司。", cta: "阅读创始人故事" },
  homeWhy: {
    tag: "为何选择 SNS",
    title: "以安全为基石，因信任而壮大",
    points: [
      { title: "机构级安全", text: "国防级实践，全程保护资本、代码与数据。" },
      { title: "政府的信赖", text: "为多个大洲的国家机构和监管者提供服务。" },
      { title: "长期合作伙伴", text: "我们投资、建设并长期陪伴客户。" },
    ],
  },
  cta: { title: "准备好一起构建安全的未来了吗？", text: "与一家将您的信任视为最宝贵资产的控股公司携手。", button: "联系我们的团队" },
  services: {
    heroTitle: "我们的服务",
    heroSubtitle: "五个相互关联的领域，为全球机构提供安全、资本与技术。",
    items: {
      cyber: { name: "网络安全", tagline: "国防级防护", description: "为金融机构、政府和企业提供端到端网络安全——威胁情报、安全架构、审计与全天候监控。", features: ["威胁检测与响应", "审计与合规", "零信任架构", "全天候监控"] },
      equity: { name: "私募股权", tagline: "有信念的资本", description: "我们投资并发展科技驱动型企业，提供长期资本、运营专长和全球网络资源。", features: ["成长资本", "运营价值创造", "全球网络资源", "长期合作"] },
      banking: { name: "核心银行软件", tagline: "现代银行的引擎", description: "我们设计并开发稳健的核心银行平台，支撑国家级的存款、贷款、支付与合规。", features: ["存贷款引擎", "实时支付", "监管合规", "云原生且可扩展"] },
      blockchain: { name: "区块链技术", tagline: "无需信任、透明、安全", description: "企业级区块链解决方案——从通证化、智能合约到面向金融的安全分布式基础设施。", features: ["智能合约开发", "通证化平台", "分布式账本基础设施", "数字资产安全"] },
      fintech: { name: "金融软件解决方案", tagline: "精心构建的金融", description: "定制金融软件——交易系统、风险分析、支付平台与数据基础设施。", features: ["交易与风险系统", "支付平台", "数据与分析", "定制集成"] },
    },
  },
  about: {
    heroTitle: "关于 SNS Infotech",
    heroSubtitle: "一家成立于2016年的全球控股机构，秉持一个简单的信念：以诚信为指引的技术与资本能够改变世界。",
    storyTag: "创始人故事",
    storyTitle: "Shrikrishna Keskar",
    storyRole: "创始人兼董事长",
    storyParas: [
      "Shrikrishna Keskar 出生并成长于印度马哈拉施特拉邦的一个小村庄。他就读于一所马拉地语公立学校，远离全球金融与科技的世界。",
      "凭借好奇心和坚定的职业精神，他自学了代码与资本的语言。2016年，他创立了 SNS Infotech，起初是一家网络安全与投资公司。",
      "如今，SNS Infotech International Holdings 业务涵盖网络安全、私募股权、核心银行、区块链和金融软件，服务全球6000多家客户，包括各国政府。",
    ],
    missionTag: "使命与愿景",
    missionTitle: "我们的使命",
    missionText: "以建立在毫不妥协的信任之上的技术，守护并赋能推动世界资本运转的机构。",
    visionTitle: "我们的愿景",
    visionText: "让每个国家、银行和企业都能对其资本与代码的安全抱有绝对信心的世界。",
    valuesTag: "我们的立场",
    valuesTitle: "我们的价值观",
    values: [
      { title: "诚信为先", text: "我们做正确的事，尤其是在无人注视时。" },
      { title: "安全为本", text: "防护绝非事后考虑，而是我们的根基。" },
      { title: "全球视野", text: "扎根本土，影响世界，尊重文化。" },
      { title: "不懈卓越", text: "我们始终以机构级标准要求自己。" },
    ],
    timelineTag: "我们的历程",
    timelineTitle: "里程碑",
    timeline: [
      { year: "2016", title: "起点", text: "在卢森堡成立，最初为网络安全与投资公司。" },
      { year: "2016", title: "全球扩张", text: "在加州沃森维尔和阿联酋迪拜设立办公室。" },
      { year: "至今", title: "6000+ 客户", text: "受到全球企业与政府的信赖。" },
    ],
  },
  global: {
    heroTitle: "全球布局",
    heroSubtitle: "总部位于卢森堡，在北美和中东设有战略办公室。",
    offices: [
      { city: "卢森堡", country: "卢森堡", type: "全球总部", desc: "我们运营的核心，位于欧洲金融中心。" },
      { city: "沃森维尔", country: "美国加州", type: "技术与创新", desc: "我们的创新中心，紧贴全球科技脉搏。" },
      { city: "迪拜", country: "阿拉伯联合酋长国", type: "区域办公室", desc: "服务中东、亚洲及新兴市场。" },
    ],
  },
  contact: {
    heroTitle: "联系我们",
    heroSubtitle: "无论您代表政府、银行还是企业，我们的团队都乐意为您提供帮助。",
    formTitle: "给我们留言",
    name: "姓名",
    email: "电子邮箱",
    company: "公司（可选）",
    subject: "主题",
    message: "您的留言",
    submit: "发送留言",
    sending: "发送中...",
    success: "谢谢！我们已收到您的留言，团队会尽快回复。",
    error: "出了点问题，请重试。",
    emailLabel: "给我们发邮件",
    hqLabel: "总部",
    officesLabel: "我们的办公室",
  },
  footer: {
    about: "一家全球控股公司，在网络安全、私募股权、银行、区块链和金融软件领域守护资本、代码与信任。",
    quickLinks: "快速链接",
    contactTitle: "联系方式",
    rights: "版权所有。",
    hq: "卢森堡（总部） · 美国沃森维尔 · 阿联酋迪拜",
  },
};

translations.mr = {
  nav: { home: "मुख्यपृष्ठ", about: "आमच्याविषयी", services: "सेवा", global: "जागतिक उपस्थिती", contact: "संपर्क" },
  common: { learnMore: "अधिक जाणून घ्या", getInTouch: "संपर्क साधा", explore: "सेवा पहा", readStory: "कहाणी वाचा", viewOffices: "कार्यालये पहा", talkToUs: "आमच्याशी बोला" },
  hero: {
    badge: "जागतिक होल्डिंग्स · स्थापना २०२६",
    title: "जगभर भांडवल, कोड आणि विश्वास सुरक्षित करत आहोत",
    subtitle: "SNS Infotech International Holdings सायबरसुरक्षा, प्रायव्हेट इक्विटी, कोअर बँकिंग, ब्लॉकचेन आणि आर्थिक सॉफ्टवेअर एका विश्वासार्ह जागतिक संस्थेत एकत्र आणते.",
    ctaPrimary: "आमच्या सेवा पहा",
    ctaSecondary: "आमची जागतिक उपस्थिती",
  },
  stats: [
    { value: "6000+", label: "समाधानी ग्राहक" },
    { value: "40+", label: "सरकारी भागीदार" },
    { value: "5", label: "प्रमुख क्षेत्रे" },
    { value: "3", label: "जागतिक कार्यालये" },
  ],
  ribbon: "सरकारे आणि जगभरातील ६०००+ ग्राहकांचा विश्वास",
  homeIntro: { tag: "आम्ही कोण आहोत", title: "उच्च-विश्वासाच्या जगासाठी आधुनिक होल्डिंग्स कंपनी", text: "लक्झेंबर्गमधील मुख्यालयापासून ते कॅलिफोर्निया आणि दुबईतील कार्यालयांपर्यंत, आम्ही बँका चालवणारे आणि राष्ट्रांना सुरक्षित करणारे तंत्रज्ञान उभारतो व पाठबळ देतो." },
  homeServices: { tag: "आम्ही काय करतो", title: "पाच क्षेत्रे. उत्कृष्टतेचा एकच मानक.", subtitle: "सर्वोच्च सुरक्षा आणि विश्वास आवश्यक असलेल्या क्षेत्रांमध्ये सखोल तज्ज्ञता." },
  homeGlobal: { tag: "आम्ही कुठे आहोत", title: "खरोखरच जागतिक उपस्थिती", subtitle: "लक्झेंबर्गमध्ये मुख्यालय, तीन खंडांमध्ये कार्यरत." },
  homeFounder: { tag: "आमचे संस्थापक", title: "महाराष्ट्रातील एका गावातून जागतिक व्यासपीठापर्यंत", text: "संस्थापक श्रीकृष्ण केसकर महाराष्ट्रातील एका छोट्या गावात लहानाचे मोठे झाले आणि मराठी माध्यमाच्या सरकारी शाळेत शिकले. २०२६ मध्ये त्यांनी SNS Infotech ची सायबरसुरक्षा व गुंतवणूक कंपनी म्हणून स्थापना केली.", cta: "संस्थापकांची कहाणी वाचा" },
  homeWhy: {
    tag: "SNS का?",
    title: "सुरक्षेवर उभारलेले, विश्वासाने विस्तारलेले",
    points: [
      { title: "संस्थात्मक दर्जाची सुरक्षा", text: "भांडवल, कोड आणि डेटा संपूर्णपणे संरक्षित करणाऱ्या संरक्षण-दर्जाच्या पद्धती." },
      { title: "सरकारांचा विश्वास", text: "अनेक खंडांमधील राष्ट्रीय संस्था व नियामकांना सेवा देतो." },
      { title: "दीर्घकालीन भागीदारी", text: "आम्ही गुंतवणूक करतो, उभारतो आणि ग्राहकांसोबत दीर्घकाळ राहतो." },
    ],
  },
  cta: { title: "एकत्र काहीतरी सुरक्षित उभारायला तयार आहात का?", text: "तुमचा विश्वास ही आपली सर्वात मौल्यवान संपत्ती मानणाऱ्या होल्डिंग्स कंपनीसोबत भागीदारी करा.", button: "आमच्या टीमशी संपर्क साधा" },
  services: {
    heroTitle: "आमच्या सेवा",
    heroSubtitle: "जगभरातील संस्थांना सुरक्षा, भांडवल आणि तंत्रज्ञान देणारी पाच परस्परसंबंधित क्षेत्रे.",
    items: {
      cyber: { name: "सायबरसुरक्षा", tagline: "संरक्षण-दर्जाचे रक्षण", description: "आर्थिक संस्था, सरकारे आणि उद्योगांसाठी संपूर्ण सायबरसुरक्षा — धोका विश्लेषण, सुरक्षित रचना, ऑडिट आणि २४/७ देखरेख.", features: ["धोका ओळख व प्रतिसाद", "ऑडिट व अनुपालन", "झिरो-ट्रस्ट रचना", "२४/७ देखरेख"] },
      equity: { name: "प्रायव्हेट इक्विटी", tagline: "दृढ विश्वासासह भांडवल", description: "आम्ही तंत्रज्ञान-आधारित कंपन्यांमध्ये गुंतवणूक करतो व त्यांची वाढ करतो — दीर्घकालीन भांडवल, कार्यचालन तज्ज्ञता आणि जागतिक नेटवर्क.", features: ["वाढीचे भांडवल", "कार्यचालन मूल्यनिर्मिती", "जागतिक नेटवर्क प्रवेश", "दीर्घकालीन भागीदारी"] },
      banking: { name: "कोअर बँकिंग सॉफ्टवेअर", tagline: "आधुनिक बँकांचे इंजिन", description: "ठेवी, कर्ज, देयके आणि अनुपालन राष्ट्रीय स्तरावर चालवणारे मजबूत कोअर बँकिंग प्लॅटफॉर्म आम्ही तयार करतो.", features: ["ठेव व कर्ज इंजिन", "रिअल-टाइम देयके", "नियामक अनुपालन", "क्लाउड-नेटिव्ह व स्केलेबल"] },
      blockchain: { name: "ब्लॉकचेन तंत्रज्ञान", tagline: "विश्वासरहित, पारदर्शक, सुरक्षित", description: "एंटरप्राइझ ब्लॉकचेन उपाय — टोकनायझेशन आणि स्मार्ट कॉन्ट्रॅक्ट्सपासून ते वित्तासाठी सुरक्षित वितरित पायाभूत सुविधांपर्यंत.", features: ["स्मार्ट कॉन्ट्रॅक्ट विकास", "टोकनायझेशन प्लॅटफॉर्म", "वितरित लेजर पायाभूत सुविधा", "डिजिटल मालमत्ता सुरक्षा"] },
      fintech: { name: "आर्थिक सॉफ्टवेअर उपाय", tagline: "अभियंत्रित वित्त", description: "सानुकूल आर्थिक सॉफ्टवेअर — ट्रेडिंग प्रणाली, जोखीम विश्लेषण, देयक प्लॅटफॉर्म आणि डेटा पायाभूत सुविधा.", features: ["ट्रेडिंग व जोखीम प्रणाली", "देयक प्लॅटफॉर्म", "डेटा व विश्लेषण", "सानुकूल एकत्रीकरण"] },
    },
  },
  about: {
    heroTitle: "SNS Infotech विषयी",
    heroSubtitle: "२०२६ मध्ये स्थापन झालेली जागतिक होल्डिंग्स संस्था, एका साध्या विश्वासावर आधारित: प्रामाणिकतेने मार्गदर्शित तंत्रज्ञान आणि भांडवल जग बदलू शकते.",
    storyTag: "संस्थापकांची कहाणी",
    storyTitle: "श्रीकृष्ण केसकर",
    storyRole: "संस्थापक व अध्यक्ष",
    storyParas: [
      "श्रीकृष्ण केसकर यांचा जन्म आणि जडणघडण महाराष्ट्रातील एका छोट्या गावात झाली. जागतिक वित्त आणि तंत्रज्ञानाच्या जगापासून दूर, ते मराठी माध्यमाच्या सरकारी शाळेत शिकले.",
      "जिज्ञासा आणि अढळ कष्टाच्या बळावर त्यांनी कोड आणि भांडवलाच्या भाषा स्वतः शिकल्या. २०२६ मध्ये त्यांनी SNS Infotech ची सायबरसुरक्षा व गुंतवणूक कंपनी म्हणून स्थापना केली.",
      "आज, SNS Infotech International Holdings सायबरसुरक्षा, प्रायव्हेट इक्विटी, कोअर बँकिंग, ब्लॉकचेन आणि आर्थिक सॉफ्टवेअर क्षेत्रांत कार्यरत असून, सरकारांसह जगभरातील ६००० हून अधिक ग्राहकांना सेवा देते.",
    ],
    missionTag: "ध्येय व दृष्टी",
    missionTitle: "आमचे ध्येय",
    missionText: "जगाचे भांडवल हलवणाऱ्या संस्थांना अटळ विश्वासावर उभारलेल्या तंत्रज्ञानाने सुरक्षित व सक्षम करणे.",
    visionTitle: "आमची दृष्टी",
    visionText: "असे जग जिथे प्रत्येक राष्ट्र, बँक आणि उद्योग आपल्या भांडवल व कोडच्या सुरक्षेवर पूर्ण विश्वासाने कार्य करू शकेल.",
    valuesTag: "आम्ही कशासाठी उभे आहोत",
    valuesTitle: "आमची मूल्ये",
    values: [
      { title: "प्रामाणिकता प्रथम", text: "आम्ही योग्य तेच करतो, विशेषतः जेव्हा कोणी पाहत नसते." },
      { title: "रचनेतच सुरक्षा", text: "संरक्षण कधीही नंतरची बाब नसते — तो आमचा पाया आहे." },
      { title: "जागतिक दृष्टिकोन", text: "स्थानिक मुळे, जागतिक प्रभाव आणि सांस्कृतिक आदर." },
      { title: "अखंड उत्कृष्टता", text: "आम्ही नेहमीच संस्थात्मक दर्जाचे मानक पाळतो." },
    ],
    timelineTag: "आमचा प्रवास",
    timelineTitle: "टप्पे",
    timeline: [
      { year: "2016", title: "सुरुवात", text: "लक्झेंबर्गमध्ये सायबरसुरक्षा व गुंतवणूक कंपनी म्हणून स्थापना." },
      { year: "2016", title: "जागतिक विस्तार", text: "वॉट्सनव्हिल, कॅलिफोर्निया आणि दुबई, यूएई येथे कार्यालये." },
      { year: "आज", title: "6000+ ग्राहक", text: "जगभरातील उद्योग आणि सरकारांचा विश्वास." },
    ],
  },
  global: {
    heroTitle: "जागतिक उपस्थिती",
    heroSubtitle: "लक्झेंबर्गमध्ये मुख्यालय, उत्तर अमेरिका आणि मध्य पूर्वेत धोरणात्मक कार्यालये.",
    offices: [
      { city: "लक्झेंबर्ग", country: "लक्झेंबर्ग", type: "जागतिक मुख्यालय", desc: "युरोपीय वित्ताच्या केंद्रस्थानी, आमच्या कार्याचे हृदय." },
      { city: "वॉट्सनव्हिल", country: "कॅलिफोर्निया, यूएसए", type: "तंत्रज्ञान व नवोन्मेष", desc: "जागतिक तंत्रज्ञानाच्या स्पंदनाजवळ, आमचे नवोन्मेष केंद्र." },
      { city: "दुबई", country: "संयुक्त अरब अमिराती", type: "प्रादेशिक कार्यालय", desc: "मध्य पूर्व, आशिया आणि उदयोन्मुख बाजारांना सेवा." },
    ],
  },
  contact: {
    heroTitle: "आमच्याशी संपर्क साधा",
    heroSubtitle: "तुम्ही सरकार, बँक किंवा उद्योगाचे प्रतिनिधित्व करत असाल — आमची टीम मदतीसाठी तयार आहे.",
    formTitle: "आम्हाला संदेश पाठवा",
    name: "पूर्ण नाव",
    email: "ईमेल पत्ता",
    company: "कंपनी (ऐच्छिक)",
    subject: "विषय",
    message: "तुमचा संदेश",
    submit: "संदेश पाठवा",
    sending: "पाठवत आहे...",
    success: "धन्यवाद! तुमचा संदेश मिळाला आहे. आमची टीम लवकरच उत्तर देईल.",
    error: "काहीतरी चुकले. कृपया पुन्हा प्रयत्न करा.",
    emailLabel: "आम्हाला ईमेल करा",
    hqLabel: "मुख्यालय",
    officesLabel: "आमची कार्यालये",
  },
  footer: {
    about: "सायबरसुरक्षा, प्रायव्हेट इक्विटी, बँकिंग, ब्लॉकचेन आणि आर्थिक सॉफ्टवेअरमध्ये भांडवल, कोड आणि विश्वास सुरक्षित करणारी जागतिक होल्डिंग्स कंपनी.",
    quickLinks: "त्वरित दुवे",
    contactTitle: "संपर्क",
    rights: "सर्व हक्क राखीव.",
    hq: "लक्झेंबर्ग (मुख्यालय) · वॉट्सनव्हिल, यूएसए · दुबई, यूएई",
  },
};

export const SERVICE_META = SERVICE_KEYS.map((key, i) => ({ key, icon: SERVICE_ICONS[i] }));
export const COMPANY = {
  name: "SNS Infotech International Holdings",
  short: "SNS Infotech",
  email: "support@snsinfotech.us",
};
